import {
  EDUCATION_TEST_ACHIEVEMENTS,
  EDUCATION_TEST_COLUMNS,
  EDUCATION_TEST_CONFIG,
  EDUCATION_TEST_FILTERS,
  EDUCATION_TEST_KNOWLEDGE_LIBRARY_ITEMS,
  EDUCATION_TEST_QUESTION_BANK_ITEMS,
  EDUCATION_TEST_QUIZ_CASE_OPTIONS,
  EDUCATION_TEST_REPORT,
  EDUCATION_TEST_ROUTES,
  EDUCATION_TEST_SORT_OPTIONS,
} from './consts';
import { COURSE_CONTENT_MOCK_COURSES } from './consts/course-content-modal.consts';
import { LESSON_BUILDER_MATERIAL_BLOCKS } from './consts/lesson-builder-modal.consts';

function cloneMockData(data) {
  return JSON.parse(JSON.stringify(data));
}

function resolveEducationMock(data) {
  return Promise.resolve(cloneMockData(data));
}

function getKnowledgeItemById(itemId) {
  return EDUCATION_TEST_KNOWLEDGE_LIBRARY_ITEMS.find((item) => String(item.id) === String(itemId)) || null;
}

function getKnowledgeItemSummaries() {
  return EDUCATION_TEST_KNOWLEDGE_LIBRARY_ITEMS.map(({ details, ...item }) => item);
}

function getLessonSectionSummaries() {
  return COURSE_CONTENT_MOCK_COURSES.map((course, index) => ({
    id: course.id,
    title: course.title || course.name || '',
    description: course.description || '',
    status: 'active',
    sortOrder: index + 1,
    metadata: {
      accessLabel: course.accessLabel,
      lessonCount: course.lessonCount,
      quizCount: course.quizCount || 0,
      gameCount: course.gameCount || 0,
      items: course.items || [],
      uiStatus: course.status || 'active',
    },
  }));
}

export function getEducationConfig() {
  return resolveEducationMock(EDUCATION_TEST_CONFIG);
}

export function getEducationRoutes() {
  return resolveEducationMock(EDUCATION_TEST_ROUTES);
}

export function getEducationFilters() {
  return resolveEducationMock(EDUCATION_TEST_FILTERS);
}

export function getEducationSortOptions() {
  return resolveEducationMock(EDUCATION_TEST_SORT_OPTIONS);
}

export function getEducationColumns() {
  return resolveEducationMock(EDUCATION_TEST_COLUMNS);
}

export function getEducationKnowledgeItems() {
  return resolveEducationMock(getKnowledgeItemSummaries());
}

export function getEducationKnowledgeItemDetails(itemId) {
  const item = getKnowledgeItemById(itemId);

  return resolveEducationMock(item?.details || null);
}

export function getEducationReport() {
  return resolveEducationMock(EDUCATION_TEST_REPORT);
}

export function getEducationQuestionBankItems() {
  return resolveEducationMock(EDUCATION_TEST_QUESTION_BANK_ITEMS);
}

export function getLessonSections() {
  return resolveEducationMock(getLessonSectionSummaries());
}

export function listTags() {
  return resolveEducationMock({
    items: [
      { id: 'manager', name: 'менеджер' },
    ],
  });
}

export function listLessonSectionItems(_context, data = {}) {
  const section = COURSE_CONTENT_MOCK_COURSES.find((course) => String(course.id) === String(data.sectionId));
  const items = (section?.items || []).map((item, index) => {
    const type = item.type === 'lesson' ? 'lesson' : 'quiz_game';
    const metadata = item.metadata || {};
    const activityType = item.type === 'game' ? 'game' : 'quiz';

    return {
      id: `mock-section-item-${item.id}`,
      sectionId: data.sectionId,
      itemType: type,
      itemId: item.id,
      sortOrder: item.sortOrder ?? index,
      canSkip: false,
      metadata,
      lesson: type === 'lesson' ? { ...item, sectionId: data.sectionId } : null,
      quizGame: type === 'quiz_game' ? {
        ...item,
        metadata: {
          ...metadata,
          activityType,
        },
      } : null,
    };
  });

  return resolveEducationMock({ items, nextCursor: null });
}

export function getLessons() {
  return resolveEducationMock([]);
}

export function getQuizCaseOptions() {
  return resolveEducationMock(EDUCATION_TEST_QUIZ_CASE_OPTIONS);
}

export function getQuizCases() {
  return resolveEducationMock([]);
}

export function getQuizGames() {
  return resolveEducationMock([]);
}

export function getQuizGameContent() {
  return resolveEducationMock({ quizGame: null, cases: [], options: [] });
}

export function getEducationAchievements() {
  return resolveEducationMock(EDUCATION_TEST_ACHIEVEMENTS);
}

let mockIdCounter = 0;

function encodeTextBase64(value = '') {
  if (typeof btoa !== 'function') return '';

  const bytes = new TextEncoder().encode(value);
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary);
}

export function createLesson(_context, data = {}) {
  return resolveEducationMock({ id: `mock-lesson-${++mockIdCounter}`, ...data });
}

export function updateLesson(_context, data = {}) {
  return resolveEducationMock({ ...data });
}

export function publishLesson(_context, data = {}) {
  return resolveEducationMock({ ...data, status: 'active' });
}

const mockLessonMaterials = LESSON_BUILDER_MATERIAL_BLOCKS.map((block, index) => ({
  ...block,
  sortOrder: index,
}));

export function listLessonMaterials() {
  return resolveEducationMock(mockLessonMaterials);
}

export function createLessonTextMaterial(_context, data = {}) {
  const kind = data.metadata?.kind || 'text';
  const material = {
    id: `mock-material-${++mockIdCounter}`,
    type: kind === 'divider' ? 'divider' : 'text',
    title: data.title || '',
    description: data.text || '',
    text: data.text || '',
    metadata: data.metadata,
    sortOrder: data.sortOrder ?? mockLessonMaterials.length + 1,
  };

  mockLessonMaterials.push(material);

  return resolveEducationMock(material);
}

export function updateLessonMaterial(_context, data = {}) {
  const materialIndex = mockLessonMaterials.findIndex((material) => (
    material.id === data.materialId || material.materialId === data.materialId
  ));
  const material = {
    ...(materialIndex >= 0 ? mockLessonMaterials[materialIndex] : {}),
    ...data,
    id: data.materialId,
    description: data.description ?? data.text ?? '',
    text: data.text ?? data.description ?? '',
  };

  if (materialIndex >= 0) {
    mockLessonMaterials.splice(materialIndex, 1, material);
  }

  return resolveEducationMock(material);
}

export function createLessonLinkMaterial(_context, data = {}) {
  const kind = data.metadata?.kind || 'link';
  const material = {
    id: `mock-material-${++mockIdCounter}`,
    type: kind === 'video' ? 'video' : 'link',
    title: data.title || '',
    description: data.description || data.href || '',
    href: data.href || '',
    metadata: data.metadata || {},
    sortOrder: data.sortOrder ?? mockLessonMaterials.length + 1,
  };

  mockLessonMaterials.push(material);

  return resolveEducationMock(material);
}

export function createLessonFileMaterial(_context, data = {}) {
  const kind = data.metadata?.kind || 'file';
  const material = {
    id: `mock-material-${++mockIdCounter}`,
    type: kind,
    title: data.title || data.fileName || '',
    fileName: data.fileName || '',
    meta: '',
    description: data.description || '',
    mimeType: data.mimeType || '',
    metadata: data.metadata,
    sortOrder: data.sortOrder ?? mockLessonMaterials.length + 1,
  };

  mockLessonMaterials.push(material);

  return resolveEducationMock(material);
}

export function uploadLessonFileMaterial(_context, data = {}) {
  const kind = data.metadata?.kind || 'file';
  const material = {
    id: `mock-material-${++mockIdCounter}`,
    type: kind,
    title: data.title || data.fileName || data.file?.name || '',
    meta: data.file?.size ? `${Math.ceil(data.file.size / 1024)} Кб` : '',
    mimeType: data.mimeType || data.file?.type || '',
    metadata: data.metadata,
    sortOrder: data.sortOrder ?? mockLessonMaterials.length + 1,
  };

  mockLessonMaterials.push(material);

  return resolveEducationMock(material);
}

export function downloadLessonMaterialContent(_context, data = {}) {
  const material = mockLessonMaterials.find((item) => item.id === data.materialId) || {};
  const text = material.text || material.description || null;

  return resolveEducationMock({
    contentBase64: text ? encodeTextBase64(text) : 'ZmlsZQ==',
    text,
    contentType: material.mimeType || (text ? 'text/plain' : 'application/octet-stream'),
    contentDisposition: material.fileName ? `attachment; filename="${material.fileName}"` : null,
    sha256: null,
    sizeBytes: text ? text.length : null,
  });
}

export function createLessonDividerMaterial(_context, data = {}) {
  const material = {
    id: `mock-material-${++mockIdCounter}`,
    type: 'divider',
    title: data.title || '',
    description: data.description || null,
    metadata: data.metadata || {},
    sortOrder: data.sortOrder ?? mockLessonMaterials.length + 1,
  };

  mockLessonMaterials.push(material);

  return resolveEducationMock(material);
}

export function detachLessonMaterial(_context, data = {}) {
  const { materialId } = data;

  mockLessonMaterials.splice(
    mockLessonMaterials.findIndex((m) => m.id === materialId),
    1,
  );

  return resolveEducationMock({ success: true });
}

export function setLessonMaterialsOrder(_context, data = {}) {
  const orderById = new Map((data.materialIds || []).map((materialId, index) => [materialId, index]));

  mockLessonMaterials.sort((a, b) => (
    (orderById.get(a.id) ?? Number.MAX_SAFE_INTEGER)
    - (orderById.get(b.id) ?? Number.MAX_SAFE_INTEGER)
  ));

  mockLessonMaterials.forEach((material, index) => {
    material.sortOrder = index + 1;
  });

  return resolveEducationMock({ success: true });
}

export function reorderLessonSectionItems(_context, data = {}) {
  return resolveEducationMock({
    items: data.items || [],
    sectionId: data.sectionId,
    success: true,
  });
}

export function attachLessonSectionItem(_context, data = {}) {
  return resolveEducationMock({ success: true, ...data });
}

export function updateLessonSectionItem(_context, data = {}) {
  return resolveEducationMock({ success: true, ...data });
}

export function createQuizCaseOption(_context, data = {}) {
  return resolveEducationMock({ id: `mock-option-${++mockIdCounter}`, ...data });
}

export function createLessonSection(_context, data = {}) {
  return resolveEducationMock({ id: `mock-section-${++mockIdCounter}`, status: 'active', ...data });
}

export function updateLessonSection(_context, data = {}) {
  const courseIndex = COURSE_CONTENT_MOCK_COURSES.findIndex((course) => String(course.id) === String(data.id));

  if (courseIndex !== -1) {
    const [course] = COURSE_CONTENT_MOCK_COURSES.splice(courseIndex, 1);
    const requestedSortOrder = Number(data.sortOrder);
    const targetIndex = Number.isFinite(requestedSortOrder)
      ? Math.min(Math.max(requestedSortOrder, 0), COURSE_CONTENT_MOCK_COURSES.length)
      : COURSE_CONTENT_MOCK_COURSES.length;

    COURSE_CONTENT_MOCK_COURSES.splice(targetIndex, 0, course);
  }

  return resolveEducationMock({ status: 'active', ...data });
}

export function createQuizCase(_context, data = {}) {
  return resolveEducationMock({ id: `mock-case-${++mockIdCounter}`, ...data });
}

export function createQuizGame(_context, data = {}) {
  return resolveEducationMock({ id: `mock-game-${++mockIdCounter}`, ...data });
}

export function activateQuizGame(_context, data = {}) {
  return resolveEducationMock({ ...data, status: 'active' });
}

export function deactivateQuizGame(_context, data = {}) {
  return resolveEducationMock({ ...data, status: 'draft' });
}

export function updateQuizGame(_context, data = {}) {
  return resolveEducationMock({ ...data });
}

export function updateQuizCase(_context, data = {}) {
  return resolveEducationMock({ ...data });
}

export function updateQuizCaseOption(_context, data = {}) {
  return resolveEducationMock({ ...data });
}

export function uploadSmoozieFile(_context, data = {}) {
  return resolveEducationMock({
    file: {
      fileName: data.fileName || data.file?.name || 'education-cover.webp',
      link: {
        path: `/integrations/files/download/mock-${++mockIdCounter}`,
      },
    },
  });
}

export const mockEducationApi = {
  getEducationConfig,
  getEducationRoutes,
  getEducationFilters,
  getEducationSortOptions,
  getEducationColumns,
  getEducationKnowledgeItems,
  getEducationKnowledgeItemDetails,
  getEducationReport,
  getEducationQuestionBankItems,
  listTags,
  getLessonSections,
  listLessonSectionItems,
  getLessons,
  getQuizCaseOptions,
  getQuizCases,
  getQuizGames,
  getQuizGameContent,
  createLesson,
  updateLesson,
  publishLesson,
  listLessonMaterials,
  createLessonTextMaterial,
  updateLessonMaterial,
  createLessonLinkMaterial,
  createLessonFileMaterial,
  uploadLessonFileMaterial,
  uploadSmoozieFile,
  downloadLessonMaterialContent,
  createLessonDividerMaterial,
  detachLessonMaterial,
  setLessonMaterialsOrder,
  reorderLessonSectionItems,
  attachLessonSectionItem,
  updateLessonSectionItem,
  createLessonSection,
  updateLessonSection,
  createQuizCaseOption,
  createQuizCase,
  createQuizGame,
  activateQuizGame,
  deactivateQuizGame,
  updateQuizGame,
  updateQuizCase,
  updateQuizCaseOption,
  getEducationAchievements,
};
